import { isDeferredProgrammedOrderItem } from "@/lib/utils";
import { isNewScheduledOrderCycleEnabledForCycle } from "@/features/newScheduledOrderCycleFeature";

/** Campos do item usados nos totais do cabeçalho de série/grupo (compatível com os dois SeriesCard). */
export interface SeriesTotalsItem {
  descricao_produto?: string | null;
  scheduling_category?: string | null;
  valor_total: number;
  valor_liquido: number;
  adjustment?: number | null;
  previousYearProductPrice?: number | null;
  is_optional?: boolean;
  quantidade_pedido?: number;
}

export interface SeriesTotals {
  /** Soma de valor_total (campo verde do cabeçalho). */
  seriesTotal: number;
  /** Soma de valor_liquido: "Preço por Aluno" do ciclo atual. */
  currentPrice: number;
  /** Soma do preço do ciclo anterior; null quando nenhum item tem preço anterior (campo oculto). */
  previousYearPrice: number | null;
}

/**
 * Preço do produto no ciclo anterior:
 * - previousYearProductPrice preenchido -> usa ele;
 * - nulo e ciclo 2026 -> volta do valor_liquido desfazendo o reajuste;
 * - nulo e ciclo > 2026 -> null (campo fica oculto).
 */
export const getPreviousYearPrice = (item: SeriesTotalsItem, cycleYear: number): number | null => {
  if (item.previousYearProductPrice != null) return item.previousYearProductPrice;
  if (cycleYear === 2026) {
    const adjustmentFactor = (item.adjustment || 0) / 100;
    return item.valor_liquido / (1 + adjustmentFactor);
  }
  return null;
};

/**
 * O item entra nos totais do cabeçalho? Na área de itens cobrados agora (`billedNowArea`) dos ciclos do novo fluxo
 * (2027+), os itens programados (2º semestre / avaliações somente programadas) ficam de fora: aparecem em "Pedido
 * programado" no resumo. Mesmo critério do total do pedido e do Resumo por Segmento. ImmediateAndExam é cobrado
 * agora e continua somando. Fora dessa área (programada/simulada) todos os itens contam.
 */
export const countsInSeriesTotals = (item: SeriesTotalsItem, cycleYear: number, billedNowArea: boolean): boolean =>
  !(billedNowArea
    && isNewScheduledOrderCycleEnabledForCycle(cycleYear)
    && isDeferredProgrammedOrderItem(item, cycleYear));

/**
 * O item entra no "Preço por Aluno" (atual e do ciclo anterior)? Produto opcional (profile_client.is_optional) só
 * entra depois que o cliente informa quantidade (≥ 1); antes disso não altera o preço, e a conta "ano anterior +
 * reajuste" continua batendo.
 */
export const countsInSeriesPrice = (item: SeriesTotalsItem): boolean =>
  !item.is_optional || (item.quantidade_pedido ?? 0) > 0;

/** Totais do cabeçalho de uma série/grupo, com a regra de exclusão em um só lugar. */
export const calculateSeriesTotals = (
  items: readonly SeriesTotalsItem[],
  cycleYear: number,
  billedNowArea: boolean,
): SeriesTotals => {
  let seriesTotal = 0;
  let currentPrice = 0;
  let previousYearPrice = 0;
  let hasAnyPreviousPrice = false;

  for (const item of items) {
    if (!countsInSeriesTotals(item, cycleYear, billedNowArea)) continue;
    seriesTotal += item.valor_total;
    if (!countsInSeriesPrice(item)) continue;
    currentPrice += item.valor_liquido;
    const previous = getPreviousYearPrice(item, cycleYear);
    if (previous !== null) {
      previousYearPrice += previous;
      hasAnyPreviousPrice = true;
    }
  }

  return { seriesTotal, currentPrice, previousYearPrice: hasAnyPreviousPrice ? previousYearPrice : null };
};
